/*
  Одно обращение: что произошло и что было сказано - §3.5, §3.3.5.

  🔴 Вид выбран владельцем 25.09.2026 из трёх макетов, вариант В: слева путь по
  шагам, справа разговор. За одним звонком тянется цепочка - выданная ссылка,
  отправленное сообщение, переход должника на его страницу, - и читается она
  сверху вниз, а не вперемешку с репликами. У сообщения правой колонки нет:
  разговора не было, вместо него текст письма и судьба ссылки.

  Три решения, за которыми стоят правила кабинета:

  1. **Путь по шагам приходит готовым с сервера.** Иначе кабинету понадобилось
     бы своё правило, что из чего следует, и свой справочник подписей
     состояний - второй экземпляр той же истины (§4.5.1).
  2. **Значения ссылки здесь нет и быть не может.** В базе живёт только её хеш,
     а в сохранённом тексте сообщения значение закрыто маской. Показывать
     нечего, и это не недоделка, а устройство доступа (§3.8.2).
  3. **Суммы форматируются тем же `money`**, что и на остальных экранах: сумма,
     вписанная в строку на сервере, выглядела бы иначе, чем соседние.
*/

import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { api } from '../api/client.js'
import { date, dateTime, money } from '../api/format.js'
import AfterCallMessage from '../components/AfterCallMessage.jsx'
import CallRecording from '../components/CallRecording.jsx'
import { Empty, Loading, Notice, Tag } from '../components/Common.jsx'
import PageBar from '../components/PageBar.jsx'
import usePaging from '../components/usePaging.js'

/*
  Состояния обращения красятся по смыслу, а не по красоте. Спокойное - то, что
  продвигает дело: согласие на рассрочку, обещание, выданная ссылка. Янтарное -
  то, что требует человека. Красный здесь не используется вовсе: он означает
  потерю денег во времени, а не отказ должника говорить (§4.5.3).
*/
const STATE_TONES = {
  acknowledged: 'calm',
  promised: 'calm',
  will_pay: 'calm',
  instalment: 'calm',
  link_issued: 'calm',
  callback: 'muted',
  objected: 'warn',
  refused_contact: 'warn',
  to_human: 'warn',
  not_settled: 'warn',
}

function Steps({ steps }) {
  return (
    <ul className="steps">
      {steps.map((step, index) => (
        /* Своего идентификатора у шага нет: путь складывается из состояний
           обращения, связанной ссылки и обещаний. Индекс здесь безопасен -
           список только на чтение, из середины ничего не удаляется, приходит
           он с сервера целиком (правило 4 скилла). */
        <li key={index} className={`steps__item${step.done ? '' : ' steps__item--waiting'}`}>
          <div>{step.title}</div>
          {step.when || step.note ? (
            <span className="steps__when">
              {[step.when ? dateTime(step.when) : null, step.note].filter(Boolean).join(' · ')}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

function Talk({ lines }) {
  return (
    <div>
      {lines.map((line, index) => (
        /* Реплики своих идентификаторов не имеют и иметь не могут: расшифровка
           хранится текстом. Список только на чтение и приходит целиком. */
        <div className="talk__line" key={index}>
          <span className={`talk__who${line.by_agent ? ' talk__who--agent' : ''}`}>{line.who}</span>
          <span>{line.text}</span>
        </div>
      ))}
    </div>
  )
}

function Message({ page }) {
  return (
    <div className="card">
      <div className="section__title">Отправленное сообщение</div>
      <p className="sms">{page.message_text}</p>
      <div className="muted">
        {[
          page.template_title ? `формулировка «${page.template_title}»` : null,
          dateTime(page.occurred_at),
          page.outcome_title,
        ]
          .filter(Boolean)
          .join(' · ')}
      </div>
      <p className="muted">
        Ссылка показана не полностью: она открывает сведения о долге, и целиком хранится только у
        должника. Нужна новая - выдайте её в карточке счёта.
      </p>
    </div>
  )
}

function Promises({ promises }) {
  // 21.10.2026: рассрочка из разговора - это до нескольких десятков взносов, поэтому
  // таблица обещаний разбита общей панелью (число записей видно всегда).
  const { rows, bar } = usePaging(promises)
  return (
    <div className="card">
      <div className="section__title">Обещания из этого разговора</div>
      <table className="table">
        <thead>
          <tr>
            <th>Что обещано</th>
            <th className="table__num">Сумма</th>
            <th>Срок</th>
            <th>Состояние</th>
          </tr>
        </thead>
        <tbody>
          {/* Своего идентификатора у обещания на этом экране нет: список
              только на чтение, приходит целиком и из середины не правится. */}
          {rows.map((promise, index) => (
            <tr key={index}>
              <td>
                {promise.instalment_number === null
                  ? 'Разовый платёж'
                  : `Взнос № ${promise.instalment_number}`}
              </td>
              <td className="table__num" data-label="Сумма">
                {money(promise.amount)}
              </td>
              <td data-label="Срок">{date(promise.due_on)}</td>
              <td className="table__block" data-label="Состояние">
                {promise.status_title}
                {/* Частичная оплата видна (решение владельца 26.10.2026). */}
                {Number(promise.paid) > 0 && promise.status !== 'kept' ? (
                  <div className="muted">
                    оплачено {money(promise.paid)} из {money(promise.amount)}
                  </div>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <PageBar {...bar} what="обещаний" />
    </div>
  )
}

export default function CommunicationPage() {
  const { communicationId } = useParams()
  const [page, setPage] = useState(null)
  const [error, setError] = useState(null)

  const load = useCallback(() => {
    setError(null)
    api.communication(communicationId).then(setPage).catch(setError)
  }, [communicationId])

  useEffect(load, [load])

  if (error) return <Notice error={error} />
  if (!page) return <Loading what="Открываем обращение" />

  const isCall = page.channel === 'call'
  return (
    <>
      <div className="head">
        <div className="head__title">{isCall ? 'Звонок должнику' : 'Сообщение должнику'}</div>
        <div className="head__note">
          <Link to={`/accounts/${page.account_id}`}>Лицевой счёт {page.account_number}</Link>
          {page.debtor_name ? ` · ${page.debtor_name}` : ''}
          {page.phone ? ` · ${page.phone}` : ''}
          {` · ${dateTime(page.occurred_at)}`}
          {page.duration_title ? ` · ${page.duration_title}` : ''}
        </div>
      </div>

      {page.states.length ? (
        <div className="card">
          <div className="row">
            {page.states.map((state) => (
              <Tag key={state.value} kind={STATE_TONES[state.value] ?? 'muted'}>
                {state.title}
              </Tag>
            ))}
          </div>
        </div>
      ) : null}

      {/* 🔴 У сообщения правой колонки нет вовсе (решение владельца
          25.09.2026): разговора там не бывает, и карточка «Разговор» с
          объяснением, почему она пуста, - это место, потраченное на ответ
          «здесь ничего нет». Вместо неё текст письма и судьба ссылки слева. */}
      <div className={`commpage${isCall ? '' : ' commpage--single'}`}>
        <div className="commpage__col">
          <div className="card">
            <div className="section__title">Что произошло</div>
            <Steps steps={page.steps} />
          </div>
          {page.message_text ? <Message page={page} /> : null}
          {page.after_call_message ? <AfterCallMessage message={page.after_call_message} /> : null}
          {page.promises.length ? <Promises promises={page.promises} /> : null}
        </div>

        {isCall ? (
          <div className="commpage__col">
            <div className="card">
              <div className="section__title">Разговор</div>
              <CallRecording
                communicationId={page.id}
                state={page.recording}
                fileName={`разговор-${page.account_number}-${page.occurred_at.slice(0, 10)}.mp3`}
              />
              {/* Запись есть, а реплик нет - разговор был, не сложилась
                  расшифровка. «Разговора не было» здесь спорило бы с
                  проигрывателем строкой выше (осмотр 06.10.2026). */}
              {page.talk.length ? (
                <Talk lines={page.talk} />
              ) : page.recording !== 'none' ? (
                <Empty title="Расшифровки нет">
                  Слова разговора не распознаны - послушайте запись выше.
                </Empty>
              ) : (
                <Empty title="Разговора не было">
                  Трубку не сняли либо сняли и положили молча. Записывать в таком звонке нечего -
                  для правила частоты он всё равно считается обращением.
                </Empty>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </>
  )
}
